import React, { useState, useEffect } from "react";
import { X, Youtube, CheckCircle2, Circle, Trophy, Clock, MapPin, NotebookPen, Save, Plus, Trash2, TrendingUp, Target } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { DAY_TYPES, BLOCK_TYPES } from "../lib/training";
import Sparkline from "./Sparkline";
import TeamLogo from "./TeamLogo";

/**
 * SessionView - client-side detail of a session, with exercise completion toggle.
 */
const AutoregBanner = ({ a }) => {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ marginTop: 14, padding: "10px 12px", border: "1px solid rgba(96,165,250,0.4)", background: "rgba(96,165,250,0.08)", fontSize: 13 }} data-testid="session-autoreg-banner">
      <button onClick={() => setOpen(!open)} style={{ background: "none", border: "none", color: "#fff", cursor: "pointer", padding: 0, textAlign: "left", fontSize: 13, width: "100%" }} data-testid="session-autoreg-toggle">
        <b>{a.coach_edited ? "Sesión ajustada por tu entrenador" : a.kind === "progress" ? "Sesión con un pequeño aumento de volumen" : "Sesión ajustada"}</b>{a.coach_edited ? "" : " según tu cuestionario de bienestar"} · mismos ejercicios, cargas adaptadas <span style={{ color: "#60a5fa" }}>{open ? "Ocultar" : "Ver cambios"}</span>
      </button>
      {open && <div style={{ marginTop: 6, color: "#d4d4d4" }}>{a.changes.map((c, i) => <div key={i}>{c.name}: {c.label} {c.before || "—"} → {c.after}</div>)}{a.reason && <div style={{ color: "#a3a3a3", marginTop: 4 }}>Motivo: {a.reason}.</div>}</div>}
    </div>
  );
};

export default function SessionView({ session, onClose, onUpdated }) {
  const [data, setData] = useState(session);
  useEffect(() => { setData(session); }, [session]);

  const dt = DAY_TYPES[data.day_type] || DAY_TYPES.fuerza;
  const dateLabel = new Date(data.date + "T00:00:00").toLocaleDateString("es-ES", { weekday: "long", day: "numeric", month: "long" });

  const completed = new Set(data.completed_exercise_ids || []);
  const totalEx = (data.blocks || []).reduce((n, b) => n + (b.exercises || []).length, 0);
  const doneEx = (data.blocks || []).reduce((n, b) => n + (b.exercises || []).filter((e) => completed.has(e.id)).length, 0);
  const pct = totalEx ? Math.round((doneEx / totalEx) * 100) : 0;

  const toggle = async (eid) => {
    const isOn = completed.has(eid);
    try {
      const { data: updated } = await api.post(`/me/sessions/${data.id}/toggle-exercise`, {
        exercise_id: eid, completed: !isOn,
      });
      setData(updated);
      onUpdated && onUpdated(updated);
    } catch { /* silent */ }
  };

  const logExercise = async (eid, fields) => {
    try {
      const { data: updated } = await api.post(`/me/sessions/${data.id}/log-exercise`, {
        exercise_id: eid, ...fields,
      });
      setData(updated);
      onUpdated && onUpdated(updated);
      toast.success("Registro guardado");
    } catch { toast.error("No se pudo guardar"); }
  };

  return (
    <div className="qv-modal-bg" onClick={onClose}>
      <div className="qv-modal" style={{ maxWidth: 760, padding: 0 }} onClick={(e) => e.stopPropagation()} data-testid="session-view">
        {/* Header hero */}
        <div style={{ background: dt.bg, borderBottom: `1px solid ${dt.border}`, padding: "22px 24px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <div>
              <div style={{ fontSize: 11, letterSpacing: "0.22em", color: dt.color, fontWeight: 700, textTransform: "uppercase", display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                <dt.icon size={14} /> {dt.label}
                {(data.secondary_day_types || []).map((sec, i) => {
                  const sdt = DAY_TYPES[sec];
                  if (!sdt) return null;
                  return (
                    <span key={i} style={{ display: "inline-flex", alignItems: "center", gap: 4, color: sdt.color, padding: "2px 8px", background: sdt.bg, border: `1px solid ${sdt.border}`, fontSize: 10, marginLeft: 4 }}>
                      <sdt.icon size={10} /> {sdt.label}
                    </span>
                  );
                })}
              </div>
              <div style={{ fontFamily: "Bebas Neue", fontSize: "1.8rem", marginTop: 6, textTransform: "capitalize" }}>{dateLabel}</div>
              {data.title && <div style={{ color: "#cfcfcf", fontSize: 13, marginTop: 4 }}>{data.title}</div>}
            </div>
            <button onClick={onClose} style={{ background: "transparent", border: "none", color: "#737373", cursor: "pointer" }}><X size={20} /></button>
          </div>

          {/* Match info if partido */}
          {(data.day_type === "partido" || data.day_type === "partido_suplente") && data.match && (data.match.rival || data.match.kickoff_time) && (
            <div style={{ marginTop: 16, padding: 14, background: "rgba(0,0,0,0.4)", border: "1px solid rgba(225,29,46,0.3)", display: "flex", alignItems: "center", gap: 14 }}>
              {data.match.rival_logo_url ? (
                <TeamLogo src={data.match.rival_logo_url} alt={data.match.rival} size={52} />
              ) : (
                <div style={{ width: 52, height: 52, background: "#0a0a0a", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Trophy size={22} color="var(--qv-red)" />
                </div>
              )}
              <div style={{ flex: 1 }}>
                <div style={{ fontFamily: "Bebas Neue", fontSize: "1.4rem" }}>vs {data.match.rival || "Rival"}</div>
                <div style={{ display: "flex", gap: 14, color: "#a3a3a3", fontSize: 12, marginTop: 4, flexWrap: "wrap" }}>
                  {data.match.kickoff_time && <span><Clock size={11} style={{ display: "inline", marginRight: 4 }} />{data.match.kickoff_time}</span>}
                  {data.match.location && <span><MapPin size={11} style={{ display: "inline", marginRight: 4 }} />{data.match.location}</span>}
                </div>
              </div>
            </div>
          )}

          {data.autoreg?.applied && <AutoregBanner a={data.autoreg} />}

          {/* Progress */}
          {totalEx > 0 && (
            <div style={{ marginTop: 16 }}>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "#a3a3a3", letterSpacing: "0.2em", textTransform: "uppercase", fontWeight: 700, marginBottom: 6 }}>
                <span>PROGRESO</span><span>{doneEx} / {totalEx}</span>
              </div>
              <div style={{ height: 6, background: "rgba(255,255,255,0.08)", borderRadius: 0, overflow: "hidden" }}>
                <div style={{ height: "100%", width: `${pct}%`, background: dt.color, transition: "width .3s ease" }} />
              </div>
            </div>
          )}
        </div>

        {/* Blocks (only for non-test days) */}
        <div style={{ padding: 18, maxHeight: "60vh", overflowY: "auto" }}>
          {data.day_type === "tests" ? (
            (data.prescribed_tests || []).length === 0 ? (
              <div className="qv-empty">No hay tests propuestos para este día.</div>
            ) : (
              (data.prescribed_tests || []).map((pt) => (
                <PrescribedTestCard
                  key={pt.id}
                  pt={pt}
                  resultId={(data.client_test_results || {})[pt.id]}
                  sessionId={data.id}
                  onSaved={(updated) => { setData(updated); onUpdated && onUpdated(updated); }}
                />
              ))
            )
          ) : (data.blocks || []).length === 0 ? (
            <div className="qv-empty">No hay ejercicios programados para este día.</div>
          ) : (
            data.blocks.map((b) => {
              const bt = BLOCK_TYPES[b.type] || BLOCK_TYPES.fuerza;
              return (
                <div key={b.id} style={{ marginBottom: 18 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10, color: bt.color, fontSize: 12, letterSpacing: "0.22em", textTransform: "uppercase", fontWeight: 700 }}>
                    <bt.icon size={16} /> {bt.label}
                    {b.name && <span style={{ color: "#737373", fontSize: 11, letterSpacing: "0.1em" }}>· {b.name}</span>}
                  </div>
                  {(b.exercises || []).length === 0 && <div style={{ color: "#737373", fontSize: 12, marginLeft: 26 }}>Sin ejercicios</div>}
                  {(b.exercises || []).map((ex) => (
                    <ExerciseRow
                      key={ex.id}
                      ex={ex}
                      done={completed.has(ex.id)}
                      log={(data.client_logs || {})[ex.id] || {}}
                      onToggle={() => toggle(ex.id)}
                      onSaveLog={(fields) => logExercise(ex.id, fields)}
                    />
                  ))}
                </div>
              );
            })
          )}
          {data.notes && (
            <div style={{ padding: 14, background: "#060606", border: "1px solid rgba(255,255,255,0.05)", fontSize: 13, color: "#cfcfcf", marginTop: 10 }}>
              <div style={{ fontSize: 10, letterSpacing: "0.22em", color: "#737373", fontWeight: 700, textTransform: "uppercase", marginBottom: 6 }}>Notas del coach</div>
              {data.notes}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ExerciseRow({ ex, done, log, onToggle, onSaveLog }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  // Build initial set rows: priority is sets_log → legacy flat fields → coach's prescribed sets count
  const buildInitialSets = () => {
    if (log.sets_log && Array.isArray(log.sets_log) && log.sets_log.length) {
      return log.sets_log.map((s) => ({ reps: s.reps || "", load: s.load || "", intensity: s.intensity || "", rest: s.rest || "" }));
    }
    if (log.reps || log.load || log.intensity || log.rest) {
      return [{ reps: log.reps || "", load: log.load || "", intensity: log.intensity || "", rest: log.rest || "" }];
    }
    const n = Math.max(1, Math.min(15, parseInt(ex.sets, 10) || 3));
    return Array.from({ length: n }, () => ({ reps: "", load: "", intensity: "", rest: "" }));
  };

  const [sets, setSets] = useState(buildInitialSets);
  const [notes, setNotes] = useState(log.notes || "");
  const [progression, setProgression] = useState(null);

  // Load progression history for this exercise name
  useEffect(() => {
    let cancel = false;
    if (!ex.name) return;
    (async () => {
      try {
        const { data } = await api.get("/me/exercises/progression", { params: { name: ex.name, limit: 8 } });
        if (!cancel) setProgression(data || []);
      } catch { /* silent */ }
    })();
    return () => { cancel = true; };
  }, [ex.name, JSON.stringify(log.sets_log || [])]);

  useEffect(() => {
    setSets(buildInitialSets());
    setNotes(log.notes || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(log)]);

  const updateSet = (i, k, v) => setSets((arr) => arr.map((s, idx) => (idx === i ? { ...s, [k]: v } : s)));
  const addSet = () => setSets((arr) => [...arr, { reps: "", load: "", intensity: "", rest: "" }]);
  const removeSet = (i) => setSets((arr) => arr.filter((_, idx) => idx !== i));

  const hasLog = (log.sets_log && log.sets_log.length > 0) ||
    (log.notes && log.notes.trim() !== "") ||
    !!(log.reps || log.load || log.intensity || log.rest);

  const save = async () => {
    setSaving(true);
    try {
      // Only keep sets that have at least one filled value
      const cleanSets = sets
        .map((s) => ({
          reps: (s.reps || "").trim(),
          load: (s.load || "").trim(),
          intensity: (s.intensity || "").trim(),
          rest: (s.rest || "").trim(),
        }))
        .filter((s) => s.reps || s.load || s.intensity || s.rest);
      await onSaveLog({ sets_log: cleanSets, notes });
      setOpen(false);
    } finally { setSaving(false); }
  };

  // For preview when collapsed
  const previewSets = (log.sets_log && log.sets_log.length > 0) ? log.sets_log : [];

  return (
    <div style={{ background: done ? "#0d1a0d" : "#060606", border: `1px solid ${done ? "rgba(34,197,94,0.35)" : "rgba(255,255,255,0.05)"}`, marginBottom: 6, transition: "background .2s ease" }} data-testid={`exercise-${ex.id}`}>
      <div style={{ display: "flex", gap: 12, padding: 12, alignItems: "flex-start" }}>
        <button onClick={onToggle} style={{ background: "transparent", border: "none", cursor: "pointer", padding: 0, marginTop: 2 }} data-testid={`toggle-${ex.id}`}>
          {done ? <CheckCircle2 size={22} color="#22C55E" /> : <Circle size={22} color="#737373" />}
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 14, textDecoration: done ? "line-through" : "none", color: done ? "#a3a3a3" : "#fff" }}>{ex.name || "—"}</div>

          {/* Coach prescription */}
          <div style={{ fontSize: 9, color: "#737373", letterSpacing: "0.22em", textTransform: "uppercase", fontWeight: 700, marginTop: 8, marginBottom: 4 }}>Pauta del coach</div>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", fontSize: 11, color: "#a3a3a3", letterSpacing: "0.08em", textTransform: "uppercase" }}>
            {ex.sets && <span>{ex.sets} series</span>}
            {ex.reps && <span>{ex.reps} reps</span>}
            {ex.load && <span>{ex.load}</span>}
            {ex.intensity && <span>{ex.intensity}</span>}
            {ex.tempo && <span>Tempo {ex.tempo}</span>}
            {ex.rest && <span>Desc. {ex.rest}</span>}
            {!ex.sets && !ex.reps && !ex.load && !ex.intensity && !ex.rest && <span style={{ color: "#555" }}>Sin pauta</span>}
          </div>
          {ex.notes && <div style={{ fontSize: 12, color: "#cfcfcf", marginTop: 6, fontStyle: "italic" }}>"{ex.notes}"</div>}

          {/* Progression chart */}
          {progression && progression.length >= 2 && (() => {
            const values = progression.map((p) => p.avg_load);
            const last = progression[progression.length - 1];
            const prev = progression[progression.length - 2];
            const delta = +(last.avg_load - prev.avg_load).toFixed(1);
            const improved = delta >= 0;
            return (
              <div style={{ marginTop: 10, padding: 10, background: "rgba(245,197,24,0.04)", border: "1px solid rgba(245,197,24,0.18)" }} data-testid={`progression-${ex.id}`}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
                  <div style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 9, color: "#EAB308", letterSpacing: "0.22em", textTransform: "uppercase", fontWeight: 700 }}>
                    <TrendingUp size={11} /> Progresión carga · {progression.length} sesiones
                  </div>
                  <div style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 10 }}>
                    <span style={{ color: "#737373" }}>Última</span>
                    <span style={{ fontFamily: "Bebas Neue", fontSize: "1.05rem", color: "#EAB308", lineHeight: 1 }}>{last.avg_load}</span>
                    <span style={{ color: improved ? "#22C55E" : "#E11D2E", fontWeight: 700 }}>
                      {delta > 0 ? "+" : ""}{delta}
                    </span>
                  </div>
                </div>
                <Sparkline values={values} color="#EAB308" height={36} />
              </div>
            );
          })()}

          {/* My log preview (collapsed) */}
          {hasLog && !open && (
            <>
              <div style={{ fontSize: 9, color: "#22C55E", letterSpacing: "0.22em", textTransform: "uppercase", fontWeight: 700, marginTop: 10, marginBottom: 4 }}>Lo que he hecho</div>
              {previewSets.length > 0 ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 2, fontSize: 11, color: "#cfcfcf" }}>
                  {previewSets.map((s, i) => (
                    <div key={i} style={{ display: "flex", gap: 8, alignItems: "baseline" }}>
                      <span style={{ color: "#22C55E", fontSize: 9, letterSpacing: "0.22em", fontWeight: 700, minWidth: 48 }}>SERIE {i + 1}</span>
                      <span style={{ letterSpacing: "0.08em", textTransform: "uppercase" }}>
                        {[s.reps && `${s.reps} reps`, s.load, s.intensity, s.rest && `Desc. ${s.rest}`].filter(Boolean).join(" · ") || "—"}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ display: "flex", gap: 12, flexWrap: "wrap", fontSize: 11, color: "#cfcfcf", letterSpacing: "0.08em", textTransform: "uppercase" }}>
                  {log.reps && <span>{log.reps} reps</span>}
                  {log.load && <span>{log.load}</span>}
                  {log.intensity && <span>{log.intensity}</span>}
                  {log.rest && <span>Desc. {log.rest}</span>}
                </div>
              )}
              {log.notes && <div style={{ fontSize: 12, color: "#cfcfcf", marginTop: 4, fontStyle: "italic" }}>"{log.notes}"</div>}
            </>
          )}
        </div>
        {ex.youtube_url && (
          <a href={ex.youtube_url} target="_blank" rel="noreferrer" className="qv-yt-btn"><Youtube size={12} /> VER</a>
        )}
      </div>

      {/* Toggle expand */}
      <div style={{ borderTop: "1px solid rgba(255,255,255,0.04)", padding: "6px 12px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <button
          onClick={() => setOpen((o) => !o)}
          data-testid={`log-toggle-${ex.id}`}
          style={{ background: "transparent", border: "none", color: hasLog ? "#22C55E" : "var(--qv-red)", fontSize: 10, letterSpacing: "0.22em", fontWeight: 700, textTransform: "uppercase", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 0" }}
        >
          <NotebookPen size={12} /> {hasLog ? "Editar mis series" : "Apuntar mis series"}
        </button>
      </div>

      {open && (
        <div style={{ padding: "0 12px 12px" }} data-testid={`log-form-${ex.id}`}>
          {/* Column headers */}
          <div style={{ display: "grid", gridTemplateColumns: "48px 1fr 1fr 1fr 1fr 28px", gap: 6, fontSize: 9, color: "#737373", letterSpacing: "0.18em", fontWeight: 700, textTransform: "uppercase", marginBottom: 6 }}>
            <div></div>
            <div>Reps</div>
            <div>Carga</div>
            <div>RPE/Int.</div>
            <div>Descanso</div>
            <div></div>
          </div>

          {sets.map((s, i) => (
            <div key={i} style={{ display: "grid", gridTemplateColumns: "48px 1fr 1fr 1fr 1fr 28px", gap: 6, marginBottom: 5, alignItems: "center" }} data-testid={`set-row-${ex.id}-${i}`}>
              <div style={{ fontFamily: "Bebas Neue", fontSize: "1rem", color: "var(--qv-red)", letterSpacing: "0.06em" }}>S{i + 1}</div>
              <input
                value={s.reps}
                onChange={(e) => updateSet(i, "reps", e.target.value)}
                placeholder={ex.reps || ""}
                data-testid={`set-reps-${ex.id}-${i}`}
                style={inputStyle}
                inputMode="numeric"
              />
              <input
                value={s.load}
                onChange={(e) => updateSet(i, "load", e.target.value)}
                placeholder={ex.load || ""}
                data-testid={`set-load-${ex.id}-${i}`}
                style={inputStyle}
              />
              <input
                value={s.intensity}
                onChange={(e) => updateSet(i, "intensity", e.target.value)}
                placeholder={ex.intensity || ""}
                data-testid={`set-intensity-${ex.id}-${i}`}
                style={inputStyle}
              />
              <input
                value={s.rest}
                onChange={(e) => updateSet(i, "rest", e.target.value)}
                placeholder={ex.rest || ""}
                data-testid={`set-rest-${ex.id}-${i}`}
                style={inputStyle}
              />
              <button
                onClick={() => removeSet(i)}
                disabled={sets.length <= 1}
                title="Eliminar serie"
                data-testid={`set-remove-${ex.id}-${i}`}
                style={{ background: "transparent", border: "none", color: sets.length <= 1 ? "#333" : "#737373", cursor: sets.length <= 1 ? "not-allowed" : "pointer" }}
              >
                <Trash2 size={13} />
              </button>
            </div>
          ))}

          <button
            onClick={addSet}
            data-testid={`set-add-${ex.id}`}
            style={{ background: "transparent", border: "1px dashed rgba(225,29,46,0.35)", color: "var(--qv-red)", padding: "6px 10px", fontSize: 10, letterSpacing: "0.22em", fontWeight: 700, textTransform: "uppercase", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4, marginTop: 4 }}
          >
            <Plus size={12} /> Añadir serie
          </button>

          <div style={{ marginTop: 12 }}>
            <div style={{ fontSize: 9, color: "#737373", letterSpacing: "0.18em", fontWeight: 700, marginBottom: 3, textTransform: "uppercase" }}>Notas (sensaciones, dolor, etc.)</div>
            <textarea
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Cómo te has sentido, molestias, cambios..."
              data-testid={`log-notes-${ex.id}`}
              style={{ width: "100%", background: "#0a0a0a", border: "1px solid rgba(255,255,255,0.08)", color: "#fff", padding: "8px 10px", fontSize: 12 }}
            />
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
            <button onClick={save} disabled={saving} className="qv-btn-primary" style={{ fontSize: 11 }} data-testid={`log-save-${ex.id}`}>
              <Save size={12} /> {saving ? "GUARDANDO..." : "GUARDAR"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const inputStyle = {
  width: "100%",
  background: "#0a0a0a",
  border: "1px solid rgba(255,255,255,0.08)",
  color: "#fff",
  padding: "7px 9px",
  fontSize: 13,
  minWidth: 0,
};


function PrescribedTestCard({ pt, resultId, sessionId, onSaved }) {
  const [result, setResult] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ value: "", unit: pt.unit || "", notes: "", client_video_url: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancel = false;
    if (!resultId) { setResult(null); return; }
    (async () => {
      try {
        const { data: tests } = await api.get("/me/tests");
        const found = (tests || []).find((t) => t.id === resultId);
        if (!cancel && found) {
          setResult(found);
          setForm({
            value: found.value != null ? String(found.value) : "",
            unit: found.unit || pt.unit || "",
            notes: found.notes || "",
            client_video_url: found.client_video_url || "",
          });
        }
      } catch { /* silent */ }
    })();
    return () => { cancel = true; };
  }, [resultId, pt.unit]);

  const submitted = !!result;

  const save = async () => {
    setSaving(true);
    try {
      const { data: entry } = await api.post(`/me/sessions/${sessionId}/submit-test-result`, {
        prescribed_test_id: pt.id,
        value: form.value === "" ? null : parseFloat(form.value),
        unit: form.unit || "",
        notes: form.notes || null,
        client_video_url: form.client_video_url || null,
      });
      setResult(entry);
      toast.success("Resultado guardado");
      setOpen(false);
      // refresh session from backend to update client_test_results
      try {
        const { data: s } = await api.get(`/me/sessions/${sessionId}`);
        onSaved && onSaved(s);
      } catch { /* noop */ }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error guardando");
    } finally { setSaving(false); }
  };

  const title = pt.test_type === "custom" ? (pt.label || "Test personalizado") : (
    pt.test_type === "vertical_jump" ? "Salto vertical" :
    pt.test_type === "sprint_10m" ? "Sprint 10m" :
    pt.test_type === "sprint_30m" ? "Sprint 30m" :
    pt.test_type === "squat_1rm" ? "Squat 1RM" :
    pt.test_type === "bench_1rm" ? "Press banca 1RM" :
    pt.test_type === "weight" ? "Peso corporal" :
    pt.test_type === "body_fat" ? "% Grasa" : pt.test_type
  );

  return (
    <div style={{ background: submitted ? "#0d1a0d" : "#070707", border: `1px solid ${submitted ? "rgba(34,197,94,0.35)" : "rgba(234,179,8,0.25)"}`, marginBottom: 10 }} data-testid={`prescribed-${pt.id}`}>
      <div style={{ padding: 14 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Target size={16} color="#EAB308" />
            <div>
              <div style={{ fontFamily: "Bebas Neue", fontSize: "1.2rem", letterSpacing: "0.04em" }}>{title}</div>
              <div style={{ fontSize: 10, color: "#737373", letterSpacing: "0.18em", textTransform: "uppercase" }}>
                {pt.unit && `Unidad: ${pt.unit}`}{pt.target && ` · Objetivo: ${pt.target}`}
              </div>
            </div>
          </div>
          {submitted && (
            <div style={{ fontFamily: "Bebas Neue", fontSize: "1.6rem", color: "#22C55E", lineHeight: 1 }}>
              {result.value != null ? <>{result.value}<span style={{ fontFamily: "Inter", fontSize: 11, color: "#a3a3a3", marginLeft: 4 }}>{result.unit}</span></> : <span style={{ fontSize: "0.85rem", color: "#22C55E" }}>NO MEDIBLE</span>}
            </div>
          )}
        </div>

        {pt.description && (
          <div style={{ marginTop: 10, padding: 10, background: "rgba(225,29,46,0.04)", borderLeft: "2px solid rgba(225,29,46,0.4)", fontSize: 12, color: "#cfcfcf", whiteSpace: "pre-wrap" }}>
            <div style={{ fontSize: 9, color: "var(--qv-red)", letterSpacing: "0.22em", textTransform: "uppercase", fontWeight: 700, marginBottom: 4 }}>Instrucciones</div>
            {pt.description}
          </div>
        )}
        {pt.tutorial_url && (
          <a href={pt.tutorial_url} target="_blank" rel="noreferrer" className="qv-yt-btn" style={{ marginTop: 8 }}>
            <Youtube size={12} /> VER DEMOSTRACIÓN
          </a>
        )}

        {submitted && !open && (
          <div style={{ marginTop: 10, padding: 10, background: "rgba(34,197,94,0.05)", border: "1px solid rgba(34,197,94,0.25)" }}>
            <div style={{ fontSize: 9, color: "#22C55E", letterSpacing: "0.22em", textTransform: "uppercase", fontWeight: 700, marginBottom: 4 }}>Tu resultado</div>
            {result.notes && <div style={{ fontSize: 12, color: "#cfcfcf", fontStyle: "italic", marginBottom: 6 }}>"{result.notes}"</div>}
            {result.client_video_url && (
              <a href={result.client_video_url} target="_blank" rel="noreferrer" className="qv-btn-ghost" style={{ fontSize: 10, borderColor: "rgba(34,197,94,0.4)", color: "#22C55E" }}>
                <Youtube size={11} /> VER MI VÍDEO
              </a>
            )}
          </div>
        )}
      </div>

      <div style={{ borderTop: "1px solid rgba(255,255,255,0.04)", padding: "6px 14px" }}>
        <button
          onClick={() => setOpen((o) => !o)}
          data-testid={`prescribed-toggle-${pt.id}`}
          style={{ background: "transparent", border: "none", color: submitted ? "#22C55E" : "#EAB308", fontSize: 10, letterSpacing: "0.22em", fontWeight: 700, textTransform: "uppercase", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 0" }}
        >
          <NotebookPen size={12} /> {submitted ? "Editar mi resultado" : "Realizar test"}
        </button>
      </div>

      {open && (
        <div style={{ padding: "0 14px 14px" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}>
            <div>
              <div style={{ fontSize: 9, color: "#737373", letterSpacing: "0.18em", fontWeight: 700, marginBottom: 3, textTransform: "uppercase" }}>Valor (vacío = no medible)</div>
              <input
                type="number"
                step="0.01"
                value={form.value}
                onChange={(e) => setForm({ ...form, value: e.target.value })}
                placeholder={pt.target || ""}
                data-testid={`prescribed-value-${pt.id}`}
                style={{ width: "100%", background: "#0a0a0a", border: "1px solid rgba(255,255,255,0.08)", color: "#fff", padding: "7px 9px", fontSize: 13 }}
              />
            </div>
            <div>
              <div style={{ fontSize: 9, color: "#737373", letterSpacing: "0.18em", fontWeight: 700, marginBottom: 3, textTransform: "uppercase" }}>Unidad</div>
              <input
                value={form.unit}
                onChange={(e) => setForm({ ...form, unit: e.target.value })}
                placeholder={pt.unit || ""}
                style={{ width: "100%", background: "#0a0a0a", border: "1px solid rgba(255,255,255,0.08)", color: "#fff", padding: "7px 9px", fontSize: 13 }}
              />
            </div>
          </div>
          <div style={{ fontSize: 9, color: "#737373", letterSpacing: "0.18em", fontWeight: 700, marginBottom: 3, textTransform: "uppercase" }}>Notas (sensaciones, condiciones)</div>
          <textarea
            rows={2}
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            placeholder="Cómo te has sentido, condiciones..."
            data-testid={`prescribed-notes-${pt.id}`}
            style={{ width: "100%", background: "#0a0a0a", border: "1px solid rgba(255,255,255,0.08)", color: "#fff", padding: "8px 10px", fontSize: 12, marginBottom: 8 }}
          />
          <div style={{ fontSize: 9, color: "#22C55E", letterSpacing: "0.18em", fontWeight: 700, marginBottom: 3, textTransform: "uppercase" }}>URL de tu vídeo (YouTube/Drive/iCloud)</div>
          <input
            value={form.client_video_url}
            onChange={(e) => setForm({ ...form, client_video_url: e.target.value })}
            placeholder="https://..."
            data-testid={`prescribed-video-${pt.id}`}
            style={{ width: "100%", background: "#0a0a0a", border: "1px solid rgba(34,197,94,0.2)", color: "#fff", padding: "7px 9px", fontSize: 12 }}
          />
          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 10 }}>
            <button onClick={save} disabled={saving} className="qv-btn-primary" style={{ fontSize: 11 }} data-testid={`prescribed-save-${pt.id}`}>
              <Save size={12} /> {saving ? "GUARDANDO..." : "GUARDAR"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
